import type { ChangeEvent, DragEvent, RefObject } from 'react';
import { useCallback, useState } from 'react';

import CloseButton from '@/components/Buttons/CloseButton/CloseButton';
import { useIsMobileViewport } from '@/hooks/useIsMobileViewport';

export type OutputFormatPreference = 'auto' | 'itinerary' | 'list';

export interface TripImportInputStepProps {
  variant: 'modal' | 'embedded';
  inputText: string;
  onInputTextChange: (value: string) => void;
  parseErrorMessage: string | null;
  isBusy: boolean;
  fileInputRef: RefObject<HTMLInputElement | null>;
  onFileChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onImportFile?: (file: File) => void;
  outputFormatPreference: OutputFormatPreference;
  onOutputFormatChange: (value: OutputFormatPreference) => void;
  onClose?: () => void;
  onImportText: () => void;
  onUploadClick: () => void;
}

function UploadDocIcon() {
  return (
    <svg width={24} height={24} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 16V8M12 8L9 11M12 8L15 11"
        stroke="#FF5212"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M6 16V17.5C6 18.3284 6.67157 19 7.5 19H16.5C17.3284 19 18 18.3284 18 17.5V16"
        stroke="#FF5212"
        strokeWidth={2}
        strokeLinecap="round"
      />
    </svg>
  );
}

export function TripImportInputStep({
  variant,
  inputText,
  onInputTextChange,
  parseErrorMessage,
  isBusy,
  fileInputRef,
  onFileChange,
  onImportFile,
  outputFormatPreference,
  onOutputFormatChange,
  onClose,
  onImportText,
  onUploadClick,
}: TripImportInputStepProps) {
  const isMobileViewport = useIsMobileViewport();
  const [isDraggingFile, setIsDraggingFile] = useState(false);

  const canCreate = Boolean(inputText.trim()) && !isBusy;

  const handleDragOver = useCallback((event: DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (!isBusy) {
      setIsDraggingFile(true);
    }
  }, [isBusy]);

  const handleDragLeave = useCallback((event: DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDraggingFile(false);
  }, []);

  const handleDrop = useCallback(
    (event: DragEvent) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDraggingFile(false);
      if (isBusy) return;
      const file = event.dataTransfer.files?.[0];
      if (!file) return;
      if (onImportFile) {
        onImportFile(file);
        return;
      }
      onUploadClick();
    },
    [isBusy, onImportFile, onUploadClick],
  );

  if (variant === 'modal') {
    const horizontalPad = isMobileViewport ? 'px-5' : 'px-8';
    const headerPadTop = isMobileViewport ? 'pt-5' : 'pt-7';
    const footerPadBottom = isMobileViewport
      ? 'pb-[calc(1.25rem+env(safe-area-inset-bottom))]'
      : 'pb-7';

    return (
      <div
        className={`flex flex-col overflow-hidden ${
          isMobileViewport ? 'min-h-0 flex-1' : 'shrink-0'
        }`}
      >
        <header
          className={`flex shrink-0 items-start justify-between gap-4 ${horizontalPad} ${headerPadTop} pr-6`}
        >
          <div className="flex min-w-0 flex-1 flex-col gap-1.5 text-[#252528]">
            <h1 className="font-lora text-[22px] font-semibold leading-[28px] tracking-[0.3px] min-[768px]:text-[26px] min-[768px]:leading-[normal]">
              Import your travel plans
            </h1>
            <p className="font-manrope text-[14px] font-normal leading-5 tracking-[0.28px] text-[#525257]">
              Upload a PDF, Word doc, or paste text. We&apos;ll turn it into an editable trip or list.
            </p>
          </div>
          {onClose ? (
            <CloseButton
              onClick={onClose}
              ariaLabel="Close import"
              size={16}
              className="!flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#f3f3f3] text-[#252528]"
            />
          ) : null}
        </header>

        <div
          className={`gt-keyboard-scroll flex flex-col gap-4 overflow-y-auto ${horizontalPad} py-6 ${
            isMobileViewport ? 'min-h-0 flex-1' : ''
          }`}
        >
          <OutputFormatPicker
            outputFormatPreference={outputFormatPreference}
            onOutputFormatChange={onOutputFormatChange}
            isBusy={isBusy}
            variant="figma"
          />

          <input
            ref={fileInputRef}
            id="trip-import-file"
            type="file"
            accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            className="sr-only"
            onChange={onFileChange}
            disabled={isBusy}
          />

          <div
            role="button"
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                if (!isBusy) onUploadClick();
              }
            }}
            onClick={() => {
              if (!isBusy) onUploadClick();
            }}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            aria-label="Upload PDF or Word document"
            className={`flex min-h-[160px] flex-col items-center justify-center gap-2 rounded-[16px] border-[1.5px] border-dashed bg-[#fdf9f6] px-4 py-6 transition-colors min-[768px]:min-h-[180px] ${
              isDraggingFile ? 'border-[#ff5212] bg-[#fff5f0]' : 'border-[#e1e1e7]'
            } ${isBusy ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
          >
            <div className="flex size-12 items-center justify-center rounded-[12px] bg-white shadow-[0_0_10px_rgba(0,0,0,0.15)]">
              <UploadDocIcon />
            </div>
            <div className="flex flex-col items-center gap-1 text-center">
              <p className="font-manrope text-[16px] font-semibold leading-[22px] tracking-[0.32px] text-[#252528]">
                Drop your doc here
              </p>
              <p className="font-manrope text-[14px] font-normal leading-5 tracking-[0.28px] text-[#525257]">
                or{' '}
                <span className="font-manrope text-[14px] font-normal text-[#ff5212] underline-offset-2 hover:underline">
                  browse files
                </span>
              </p>
              <p className="font-manrope text-[12px] font-normal leading-none tracking-[0.24px] text-[#a7a7ae]">
                PDF or Word (.docx) · up to 20 MB
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="h-px flex-1 bg-[#e1e1e7]" aria-hidden />
            <span className="font-manrope text-[12px] font-normal tracking-[0.24px] text-[#a7a7ae]">
              OR PASTE TEXT
            </span>
            <div className="h-px flex-1 bg-[#e1e1e7]" aria-hidden />
          </div>

          <div className="flex flex-col gap-2">
            <div className="flex min-h-[140px] flex-col rounded-[16px] border border-solid border-[#e1e1e7] bg-[#fafafa] px-4 py-3.5 min-[768px]:min-h-[172px]">
              <label className="sr-only" htmlFor="trip-import-text">
                Paste itinerary or place list
              </label>
              <textarea
                id="trip-import-text"
                aria-label="Itinerary or place list text"
                value={inputText}
                onChange={(event) => onInputTextChange(event.target.value)}
                rows={5}
                placeholder="Paste lists, notes, an itinerary, blog post or a doc…"
                className="gt-keyboard-scroll min-h-[112px] w-full flex-1 resize-none border-0 bg-transparent font-manrope text-[14px] font-normal leading-5 tracking-[0.28px] text-[#252528] outline-none ring-0 placeholder:text-[#a7a7ae]"
                disabled={isBusy}
              />
            </div>
            {parseErrorMessage ? (
              <p role="alert" className="font-manrope text-[14px] font-normal leading-5 text-[#e04710]">
                {parseErrorMessage}
              </p>
            ) : null}
          </div>
        </div>

        <footer
          className={`flex shrink-0 items-center justify-between gap-3 border-t border-transparent ${horizontalPad} pt-5 ${footerPadBottom}`}
        >
          <button
            type="button"
            disabled={isBusy}
            onClick={onClose}
            className="inline-flex h-11 items-center justify-center rounded-full bg-[#e1e1e7] px-6 font-manrope text-[16px] font-semibold leading-[22px] tracking-[0.32px] text-[#525257] transition-opacity disabled:cursor-not-allowed disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!canCreate}
            onClick={onImportText}
            className="inline-flex h-11 items-center justify-center rounded-full bg-[#252528] px-7 font-manrope text-[16px] font-semibold leading-[22px] tracking-[0.32px] text-white shadow-[0_0_20px_rgba(0,0,0,0.15)] transition-opacity hover:bg-[#1a1a1d] disabled:cursor-not-allowed disabled:opacity-30"
          >
            {isBusy ? 'Creating…' : 'Create'}
          </button>
        </footer>
      </div>
    );
  }

  return (
    <div className="gt-keyboard-scroll flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto">
      <h2 className="font-lora text-xl font-semibold text-gray-900">Import a trip or list</h2>
      <p className="font-manrope text-sm text-gray-600">
        Upload a PDF or Word itinerary, or paste text. We&apos;ll structure days and places for you.
      </p>
      {parseErrorMessage ? (
        <p role="alert" className="font-manrope text-sm text-red-700">
          {parseErrorMessage}
        </p>
      ) : null}
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.docx"
        className="font-manrope text-sm"
        onChange={onFileChange}
        disabled={isBusy}
      />
      <textarea
        aria-label="Itinerary or place list text"
        value={inputText}
        onChange={(event) => onInputTextChange(event.target.value)}
        rows={8}
        className="rounded-lg border border-gray-200 p-3 font-manrope text-sm"
        disabled={isBusy}
      />
      <OutputFormatPicker
        outputFormatPreference={outputFormatPreference}
        onOutputFormatChange={onOutputFormatChange}
        isBusy={isBusy}
      />
      <button
        type="button"
        disabled={isBusy || !inputText.trim()}
        onClick={onImportText}
        className="rounded-button bg-orange-500 px-4 py-3 font-manrope text-base text-white disabled:opacity-40"
      >
        Create
      </button>
    </div>
  );
}

function OutputFormatPicker({
  outputFormatPreference,
  onOutputFormatChange,
  isBusy,
  variant = 'default',
}: {
  outputFormatPreference: OutputFormatPreference;
  onOutputFormatChange: (value: OutputFormatPreference) => void;
  isBusy: boolean;
  variant?: 'default' | 'figma';
}) {
  const labelClass =
    variant === 'figma'
      ? 'font-manrope text-[16px] font-normal leading-[22px] tracking-[0.32px] text-[#525257]'
      : 'font-manrope text-[14px] font-semibold text-[#252528]';

  return (
    <div className="flex flex-col gap-2">
      <p className={labelClass}>Output format</p>
      <div role="radiogroup" aria-label="Output format" className="flex flex-wrap gap-2">
        {(
          [
            ['auto', 'Auto'],
            ['itinerary', 'Trip'],
            ['list', 'List'],
          ] as const
        ).map(([value, label]) => {
          const selected = outputFormatPreference === value;
          return (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={selected}
              disabled={isBusy}
              className={
                variant === 'figma'
                  ? `rounded-full px-4 py-1.5 font-manrope text-[14px] font-semibold leading-5 tracking-[0.28px] transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
                      selected
                        ? 'bg-[#ff5212] text-white'
                        : 'border border-[#e1e1e7] bg-white text-[#525257]'
                    }`
                  : `rounded-full px-3 py-1.5 font-manrope text-sm ${
                      selected ? 'bg-[#ff5212] text-white' : 'bg-gray-100 text-gray-800'
                    }`
              }
              onClick={() => onOutputFormatChange(value)}
            >
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
